import Banner from '@/components/common/Banner';
import { Button } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    AlertCircle,
    Bank,
    ChevronRight,
    Plus,
} from '@/components/common/icons';
import { TrSkeletons } from '@/components/common/Skeletons';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Subheader, Text } from '@/components/common/Typography';
import { FinancialEntityIcon } from '@/components/financialEntities/FinancialEntityIcon';
import {
    OverlappingAvatars,
    SingleAvatar,
} from '@/components/users/UserAvatars';
import { toDollarsWithCents } from '@/util/currency';
import {
    Box,
    FlexProps,
    Skeleton,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tooltip,
    Tr,
} from '@chakra-ui/react';
import { getName } from '@common/financialEntities';
import { Goal } from '@common/goals';
import { useAtomicEnabled } from '@common/react/atomic';
import {
    getGoalTransferTitleStr,
    GoalTransferEntity,
    isFailedGoalTransfer,
    useGoalTransferEntities,
} from '@common/react/goals';
import { getPartnerName, useCurrentUser } from '@common/react/user';
import { useGoalTransfersShutdownMessage } from '@common/shutdown';
import { getRecurrenceConfig } from '@common/types';
import { dt } from '@common/util/dateTime';
import { showGoalScheduledTransactionStatusModal } from '../GoalScheduledTransactionStatusModal';
import { showGoalTransactionStatusModal } from '../GoalTransactionStatusModal';
import { useStartGoalDepositFlow } from '../helpers';
import { useGoalDetailContext } from './GoalDetailContext';

export default function GoalDetailTransfersSection(props: FlexProps) {
    const { goalLoading, goal } = useGoalDetailContext();
    const transferEntities = useGoalTransferEntities(goal);
    const shutdownMessage = useGoalTransfersShutdownMessage({ goal });
    const { isAtomicEnabled, atomicDisabledMessage } = useAtomicEnabled();
    const isLoading = goalLoading || !goal;

    return (
        <FlexCol pt={8} pb={4} px={16} {...props}>
            <FlexRow justify="space-between" align="center" mb={6}>
                {isLoading ? (
                    <Skeleton w={40} h={8} />
                ) : (
                    <Subheader>
                        {getGoalTransferTitleStr(transferEntities)}
                    </Subheader>
                )}
                {isLoading ? (
                    <Skeleton w={24} h={8} />
                ) : (
                    <GoalDepositButton goal={goal} />
                )}
            </FlexRow>
            <Table>
                <Thead>
                    <Tr>
                        <Th textAlign="center" w={32} pl={2}>
                            TRANSFER ON
                        </Th>
                        <Th textAlign="center"></Th>
                        <Th textAlign="center">FROM</Th>
                        <Th textAlign="center">TO</Th>
                        <Th textAlign="center" pr={0}>
                            AMOUNT
                        </Th>
                        <Th w={4} pl={0} pr={2}></Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {isLoading ? (
                        <>
                            <TrSkeletons count={5} h={6} />
                            <TrSkeletons count={5} h={6} />
                            <TrSkeletons count={5} h={6} />
                        </>
                    ) : (
                        transferEntities.map((te) => (
                            <GoalTransferRow
                                key={te.id}
                                transferEntity={te}
                                goal={goal}
                            />
                        ))
                    )}
                </Tbody>
            </Table>
            {transferEntities.length === 0 ? (
                <FlexRow
                    justify="center"
                    align="center"
                    p={4}
                    rounded="md"
                    bg="gray.50"
                >
                    <Text fontSize="sm" color="gray.400">
                        No upcoming transfers
                    </Text>
                </FlexRow>
            ) : (
                <></>
            )}
            {shutdownMessage && (
                <Banner icon={<AlertCircle />} mt={8}>
                    {shutdownMessage}
                </Banner>
            )}
            {!isAtomicEnabled && (
                <Banner icon={<AlertCircle />} mt={8} bg="yellow.50">
                    {atomicDisabledMessage}
                </Banner>
            )}
        </FlexCol>
    );
}

function GoalDepositButton({ goal }: { goal: Goal }) {
    const startGoalDepositFlow = useStartGoalDepositFlow(goal);
    const { isAtomicEnabled } = useAtomicEnabled();

    // Hide button if Atomic is disabled or deposit flow not available
    if (!isAtomicEnabled || !startGoalDepositFlow) return <></>;

    return (
        <Button
            colorScheme="dark"
            variant="outline"
            size="sm"
            rounded="full"
            onClick={startGoalDepositFlow}
            onClickEvent="Deposit Money Button Clicked"
        >
            <Box as="span" mr={5}>
                Deposit money
            </Box>
            <Plus size="sm" strokeWidth={2} />
        </Button>
    );
}

interface GoalTransferRowProps {
    transferEntity: GoalTransferEntity;
    goal: Goal;
}

function GoalTransferRow({ transferEntity, goal }: GoalTransferRowProps) {
    const { partnerInfo } = useCurrentUser();

    let nextTransfer: React.ReactNode | undefined;
    if (transferEntity.nextTransferDate) {
        nextTransfer = dt(transferEntity.nextTransferDate).format(
            'MMM D, YYYY'
        );
    }

    if (!nextTransfer) return <></>;

    let avatar;
    let prefix;
    let description;
    let sign;

    if (goal.accountStructure === 'JOINT') {
        avatar = <OverlappingAvatars size="xs" />;
        prefix = 'Our';
    } else if (goal.accountStructure === 'SPLIT' && !transferEntity.isMine) {
        avatar = <SingleAvatar size="xs" isPartner />;
        prefix = `${getPartnerName(partnerInfo)}’s`;
    } else {
        avatar = <SingleAvatar size="xs" />;
        prefix = 'Your';
    }
    if (transferEntity.__typename === 'TransactionSchedule') {
        const adverb = getRecurrenceConfig(
            transferEntity.recurrence
        ).adverbPhrase;
        description = adverb + ' deposit';
        sign = '+';
    } else if (transferEntity.transactionSchedule) {
        // Show transactions created from transaction schedules as "recurring"
        const adverb = getRecurrenceConfig(
            transferEntity.transactionSchedule.recurrence
        ).adverbPhrase;
        description = adverb + ' deposit';
        sign = '+';
    } else {
        description = 'one time ' + transferEntity.direction.toLowerCase();
        sign = transferEntity.direction === 'DEPOSIT' ? '+' : '-';
    }

    const openDetailModal = () => {
        if (transferEntity.__typename === 'Transaction') {
            showGoalTransactionStatusModal({
                transactionId: transferEntity.id,
                transactionDirection: transferEntity.direction,
            });
        } else {
            // TODO: Handle this case. Shouldn't be possible.
            if (!transferEntity.sourceId) return;

            showGoalScheduledTransactionStatusModal({
                sourceId: transferEntity.sourceId,
                destinationId: transferEntity.destinationId,
                transactionSchedule: transferEntity,
                goalId: goal.id,
            });
        }
    };

    const isFailed = isFailedGoalTransfer(transferEntity);
    const title = [prefix, description].join(' ') + (isFailed ? ' failed' : '');

    return (
        <Tr {...hoverStyleProps({ bg: 'gray.100' })} onClick={openDetailModal}>
            <Td textAlign="center" w={32} pl={2} whiteSpace="nowrap">
                {nextTransfer}
            </Td>
            <Td>
                <FlexRow align="center" gap={2}>
                    {avatar}
                    <Text>{title}</Text>
                    {isFailed && <AlertCircle color="red.500" size="md" />}
                </FlexRow>
            </Td>
            <Td>
                {transferEntity.source ? (
                    <Tooltip
                        placement="top"
                        label={getName(transferEntity.source)}
                    >
                        <FlexRow justify="center">
                            <FinancialEntityIcon fe={transferEntity.source} />
                        </FlexRow>
                    </Tooltip>
                ) : (
                    <FlexRow justify="center">
                        <Bank />
                    </FlexRow>
                )}
            </Td>
            <Td>
                {transferEntity.destination ? (
                    <Tooltip
                        placement="top"
                        label={getName(transferEntity.destination)}
                    >
                        <FlexRow justify="center">
                            <FinancialEntityIcon
                                fe={transferEntity.destination}
                            />
                        </FlexRow>
                    </Tooltip>
                ) : (
                    <FlexRow justify="center">
                        <Bank />
                    </FlexRow>
                )}
            </Td>
            <Td
                textAlign="center"
                pr={0}
                textDecoration={isFailed ? 'line-through' : undefined}
                color={isFailed ? 'textMuted' : undefined}
            >
                {sign}
                {toDollarsWithCents(transferEntity.amount)}
            </Td>
            <Td w={4} pl={0} pr={1}>
                <FlexRow justify="center" align="center">
                    <ChevronRight size="sm" />
                </FlexRow>
            </Td>
        </Tr>
    );
}
